/* sugar:assets/vendor-runtime-loader.js@2.0.0 */
/*
 * Sugar vendor runtime loader
 *
 * Loads third-party runtimes only on pages whose DOM actually needs them, so a
 * page without sliders/accordions ships none of their bytes:
 *
 *   - Splide (+ AutoScroll extension + CSS)  when a `.splide` / `[data-needs-splide]` element exists
 *   - Alpine.js (+ Collapse plugin)          when x-* / @ directives exist
 *
 * Self-hosted asset URLs come from data-* attributes on this script's own tag
 * (see layout/theme.liquid); anything missing falls back to the CDN.
 *
 * A layout can also hand it first-party engines to load the same way, one pair of
 * attributes per engine:
 *
 *   data-lazy-<key>="<asset url>"  data-lazy-<key>-when="<css selector>"
 *
 * The engine is fetched once the DOM (initial or later-arriving) matches its
 * selector, and `document` gets a `sugar:runtime` event with `{ key }` when it
 * has run. `window.SugarVendor.load(key)` requests one directly. When the key is
 * `sugar-interactive`, callbacks queued in `window.__sugarReadyQ` (a layout's
 * `SugarReady` shim) run after it has booted. layout/landing.liquid uses this;
 * layout/theme.liquid loads those engines up front and passes nothing.
 *
 * `data-slider-chrome` / `data-slider-chrome-css` name the shared slider chrome
 * (assets/slider-chrome.js/.css). When given, both are loaded ahead of Splide,
 * because hosts only attach the chrome if it already exists at mount time.
 *
 * A MutationObserver re-scans DOM that arrives later (theme editor section
 * injection, cart drawer re-renders, section rendering API), so consumers keep
 * their existing "poll until `typeof Splide !== 'undefined'`" pattern unchanged.
 *
 * A script a page needs only after its first second (search suggestions, the cart's
 * discount box, counters and forms further down) is written inert,
 *
 *   <script type="sugar/later" data-src="<asset url>"></script>
 *
 * and started once the page has loaded or the shopper first taps, types or scrolls,
 * whichever comes first, in document order and once per URL. One that arrives after
 * that (the theme editor adding a block) starts straight away. Such a script must not
 * wait for DOMContentLoaded alone, which has fired by then.
 *
 * Consumers that only need an engine under a runtime condition (e.g. the
 * Grid + Mobile Slider needs Splide on mobile only) call
 * `window.SugarVendor.loadSplide()` directly — it returns a Promise.
 */
(function () {
  var scriptEl = document.currentScript || document.querySelector('script[src*="vendor-runtime-loader"]');
  var cfg = (scriptEl && scriptEl.dataset) || {};

  var URLS = {
    splide: cfg.splide || '',
    splideCss: cfg.splideCss || '',
    splideAutoScroll: cfg.splideAutoscroll || '',
    alpine: cfg.alpine || 'https://cdn.jsdelivr.net/npm/alpinejs@3.x.x/dist/cdn.min.js',
    alpineCollapse: cfg.alpineCollapse || 'https://cdn.jsdelivr.net/npm/@alpinejs/collapse@3.x.x/dist/cdn.min.js'
  };

  var CHROME = {
    js: cfg.sliderChrome || '',
    css: cfg.sliderChromeCss || ''
  };

  // First-party engines this layout chose to load on demand: key -> { src, when, promise }.
  var LAZY = {};
  (function () {
    var attrs = scriptEl ? scriptEl.attributes : [];
    for (var i = 0; i < attrs.length; i++) {
      var name = attrs[i].name;
      if (name.indexOf('data-lazy-') !== 0 || /-when$/.test(name)) continue;
      var key = name.slice('data-lazy-'.length);
      LAZY[key] = {
        src: attrs[i].value,
        when: scriptEl.getAttribute(name + '-when') || '',
        promise: null
      };
    }
  })();

  var loadedScripts = {};
  var loadedStyles = {};

  function loadScript(key, src) {
    if (window.__swOk === false) return Promise.reject(new Error('x'));
    if (loadedScripts[key]) return loadedScripts[key];

    loadedScripts[key] = new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-runtime-lib="' + key + '"]');
      if (existing) {
        if (existing.getAttribute('data-loaded') === 'true') {
          resolve();
          return;
        }

        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      var script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.setAttribute('data-runtime-lib', key);

      script.addEventListener(
        'load',
        function () {
          script.setAttribute('data-loaded', 'true');
          resolve();
        },
        { once: true }
      );
      script.addEventListener('error', reject, { once: true });

      document.head.appendChild(script);
    });

    return loadedScripts[key];
  }

  function loadStyle(key, href) {
    if (!href || loadedStyles[key]) return;
    if (document.querySelector('link[data-runtime-lib="' + key + '"]')) {
      loadedStyles[key] = true;
      return;
    }

    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-runtime-lib', key);
    document.head.appendChild(link);
    loadedStyles[key] = true;
  }

  /* ------------------------------------------------------------------ */
  /* Splide                                                              */
  /* ------------------------------------------------------------------ */

  var splidePromise = null;

  function loadSplide() {
    if (window.Splide) return Promise.resolve();
    if (splidePromise) return splidePromise;
    if (!URLS.splide) return Promise.reject(new Error('splide url not configured'));

    document.documentElement.setAttribute('data-splide-runtime-loading', 'true');
    loadStyle('splide-css', URLS.splideCss);
    loadStyle('slider-chrome-css', CHROME.css);

    // Chrome and extension first: hosts read `window.SugarSliderChrome` and
    // `window.splide.Extensions` synchronously as soon as `Splide` exists, so
    // both must already be registered by then.
    var chrome = CHROME.js && !window.SugarSliderChrome
      ? loadScript('slider-chrome', CHROME.js).catch(function () {})
      : Promise.resolve();

    splidePromise = chrome
      .then(function () {
        return URLS.splideAutoScroll
          ? loadScript('splide-auto-scroll', URLS.splideAutoScroll).catch(function () {})
          : null;
      })
      .then(function () {
        return loadScript('splide', URLS.splide);
      })
      .finally(function () {
        document.documentElement.setAttribute('data-splide-runtime-loaded', 'true');
      });

    return splidePromise;
  }

  function maybeLoadSplide(root) {
    if (window.Splide || splidePromise) return;

    var scope = root && root.querySelector ? root : document;
    // NOTE: not `[data-splide]` — that would match this loader's own <script data-splide=...> tag.
    var selector = '.splide, [data-needs-splide]';
    var hasRoot = !!(scope.matches && scope.matches(selector));
    var hasDescendant = !!scope.querySelector(selector);
    if (!hasRoot && !hasDescendant) return;

    loadSplide().catch(function () {});
  }

  /* ------------------------------------------------------------------ */
  /* Alpine                                                              */
  /* ------------------------------------------------------------------ */

  function hasAlpineMarkup(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var elements = scope.querySelectorAll('*');
    var i;
    var j;

    for (i = 0; i < elements.length; i++) {
      var attrs = elements[i].attributes;
      for (j = 0; j < attrs.length; j++) {
        var name = attrs[j].name;
        if (name === 'x-data' || name.indexOf('x-') === 0 || name.charAt(0) === '@') {
          return true;
        }
      }
    }

    return false;
  }

  function maybeLoadAlpine(root) {
    if (document.documentElement.hasAttribute('data-alpine-runtime-loading')) return;
    if (window.Alpine) return;
    if (!hasAlpineMarkup(root)) return;

    document.documentElement.setAttribute('data-alpine-runtime-loading', 'true');
    // Collapse plugin must register before Alpine core boots (it hooks alpine:init).
    loadScript('alpine-collapse', URLS.alpineCollapse)
      .then(function () {
        return loadScript('alpine-core', URLS.alpine);
      })
      .catch(function () {})
      .finally(function () {
        document.documentElement.setAttribute('data-alpine-runtime-loaded', 'true');
      });
  }

  /* ------------------------------------------------------------------ */
  /* First-party engines a layout loads on demand                        */
  /* ------------------------------------------------------------------ */

  function drainReadyQueue() {
    var queue = window.__sugarReadyQ;
    if (!queue || !queue.length) return;
    window.__sugarReadyQ = [];
    for (var i = 0; i < queue.length; i++) {
      try {
        queue[i]();
      } catch (e) {}
    }
  }

  function loadLazy(key) {
    var entry = LAZY[key];
    if (!entry) return Promise.reject(new Error('unknown runtime: ' + key));
    if (entry.promise) return entry.promise;

    entry.promise = loadScript('lazy-' + key, entry.src).then(function () {
      if (key === 'sugar-interactive') drainReadyQueue();
      document.dispatchEvent(new CustomEvent('sugar:runtime', { detail: { key: key } }));
    });
    return entry.promise;
  }

  function maybeLoadLazy(root) {
    var scope = root && root.querySelector ? root : document;
    var key;

    for (key in LAZY) {
      if (!Object.prototype.hasOwnProperty.call(LAZY, key)) continue;
      var entry = LAZY[key];
      if (entry.promise || !entry.when) continue;
      var hasRoot = !!(scope.matches && scope.matches(entry.when));
      if (!hasRoot && !scope.querySelector(entry.when)) continue;
      loadLazy(key).catch(function () {});
    }

    // Something called the layout's SugarReady shim before the engine existed.
    if (LAZY['sugar-interactive'] && window.__sugarReadyQ && window.__sugarReadyQ.length) {
      loadLazy('sugar-interactive').catch(function () {});
    }
  }

  function allLazyLoaded() {
    for (var key in LAZY) {
      if (Object.prototype.hasOwnProperty.call(LAZY, key) && !LAZY[key].promise) return false;
    }
    return true;
  }

  /* ------------------------------------------------------------------ */
  /* Later scripts                                                       */
  /* ------------------------------------------------------------------ */

  var LATER = 'script[type="sugar/later"]';
  var LATER_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll'];
  var laterReleased = false;
  var laterStarted = {};

  function startLater(root) {
    if (!laterReleased || window.__swOk === false) return;
    var scope = root && root.querySelectorAll ? root : document;
    var tags = Array.prototype.slice.call(scope.querySelectorAll(LATER));
    if (scope.matches && scope.matches(LATER)) tags.unshift(scope);
    for (var i = 0; i < tags.length; i++) {
      var src = tags[i].getAttribute('data-src');
      if (!src || laterStarted[src]) continue;
      laterStarted[src] = true;
      var script = document.createElement('script');
      script.src = src;
      // Inserted scripts run as soon as they arrive; this keeps them in document order.
      script.async = false;
      document.head.appendChild(script);
    }
  }

  function releaseLater() {
    if (laterReleased) return;
    laterReleased = true;
    for (var i = 0; i < LATER_EVENTS.length; i++) {
      window.removeEventListener(LATER_EVENTS[i], releaseLater, true);
    }
    startLater(document);
  }

  if (document.readyState === 'complete') {
    laterReleased = true;
  } else {
    window.addEventListener('load', releaseLater, { once: true });
    for (var le = 0; le < LATER_EVENTS.length; le++) {
      window.addEventListener(LATER_EVENTS[le], releaseLater, { capture: true, passive: true });
    }
  }
  document.addEventListener('shopify:section:load', function (event) {
    startLater(event.target);
  });

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */

  function boot(root) {
    maybeLoadSplide(root);
    maybeLoadAlpine(root);
    maybeLoadLazy(root);
    startLater(root);
  }

  function allLoaded() {
    return !!(window.Splide && window.Alpine) && allLazyLoaded();
  }

  function init() {
    boot(document);

    var observer = new MutationObserver(function (mutations) {
      var i;
      var j;

      for (i = 0; i < mutations.length; i++) {
        var nodes = mutations[i].addedNodes;
        for (j = 0; j < nodes.length; j++) {
          var node = nodes[j];
          if (!node || node.nodeType !== 1) continue;
          boot(node);
        }
      }

      if (allLoaded()) {
        observer.disconnect();
      }
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  window.SugarVendor = {
    loadSplide: loadSplide,
    loadScript: loadScript,
    load: loadLazy
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
